import { type FC, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import FeishuIcon from "src/assets/feishu-switch.svg";
import { formatMessage } from "react-intl-universal";
import useQuery from "../../hooks/use-query";
import { feishuCallback } from "../../api/api";
import { isInFeishu } from "../../common/client-platform";
import { Messenger } from "../../common/messenger";
import useFeishuClientAutoLogin from "../../hooks/use-feishu-client-auto-login";
import {
  buildFeishuBridgeRedirectUri,
  buildFeishuGotoUrl,
  buildFeishuIframeSrc,
  getAndPersistFeishuNonce,
  isFeishuCallbackStateValid,
} from "../../common/feishu-login";

export interface FeishuLoginProps {}

const FeishuLogin: FC<FeishuLoginProps> = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const inFeishuClient = isInFeishu();
  const title = formatMessage({ id: "title.feishu_qrcode_login" });
  const appId = query.get('appid') ?? '';
  const stateRef = useRef("");
  const callbackIframeRef = useRef<HTMLIFrameElement | null>(null);
  const feishuClientStateRef = useRef(getAndPersistFeishuNonce());
  const { onSuccess: onFeishuClientLoginSuccess, tryFeishuClientAutoLogin } =
    useFeishuClientAutoLogin(
      process.env.REACT_APP_FEISHU_APPID || "",
      feishuClientStateRef.current
    );

  const doFetchTokenByCode = useCallback(
    (code: string, appId: string, state: string) => {
      feishuCallback({
        code,
        client_id: appId,
        state,
        fs_redirect_uri: buildFeishuBridgeRedirectUri({
          origin: window.location.origin,
          platformId: appId,
        }),
      })
        .then((responseData) => {
          const token = responseData.user_token;

          if (!token) {
            throw new Error("Feishu login did not return a user token");
          }

          const path = `/success?appid=${appId}&token=${encodeURIComponent(token)}`;
          navigate(path);
          Messenger.getInstance().notifyLoginSuccess(token, 0);
        })
        .catch((error: Error) => {
          toast.error(error.message);
        });
    },
    [navigate]
  );

  useEffect(() => {
    onFeishuClientLoginSuccess((code) => {
      doFetchTokenByCode(code, appId, feishuClientStateRef.current);
    });
  }, [appId, doFetchTokenByCode, onFeishuClientLoginSuccess]);

  useEffect(() => {
    tryFeishuClientAutoLogin();
  }, [tryFeishuClientAutoLogin]);

  useEffect(() => {
    const previousOnCodeReceived = window.onCodeReceived;

    window.onCodeReceived = function (data) {
      if (data.platformId && data.platformId !== (query.get("appid") ?? "")) {
        return;
      }

      if (!isFeishuCallbackStateValid(stateRef.current, data.state)) {
        toast.error("Feishu login state mismatch");
        return;
      }

      doFetchTokenByCode(data.code, data.platformId, stateRef.current);

      if (callbackIframeRef.current) {
        callbackIframeRef.current.src = "about:blank";
      }
    };

    return () => {
      window.onCodeReceived = previousOnCodeReceived;
    };
  }, [doFetchTokenByCode, query]);
  
  useEffect(() => {
    if (inFeishuClient) {
      return;
    }

    let handleMessage: ((event: MessageEvent) => void) | null = null;
    const callbackIframe = callbackIframeRef.current;

    const initFeishuLogin = () => {
      // @ts-ignore
      if (window.QRLogin) {
        const state = getAndPersistFeishuNonce();
        const redirectUri = buildFeishuBridgeRedirectUri({
          origin: window.location.origin,
          platformId: appId,
        });
        const gotoUrl = buildFeishuGotoUrl({
          feishuAppId: process.env.REACT_APP_FEISHU_APPID || "",
          redirectUri,
          state,
        });
        stateRef.current = state;

        // @ts-ignore
        const QRLoginObj = window.QRLogin({
          id: "pudu-onelogin-feishu-qrcode-root",
          goto: gotoUrl,
          width: "300",
          height: "300",
          style: "width:300px;height:300px;border:none;"
        });

        handleMessage = (event: MessageEvent) => {
          // @ts-ignore
          if (QRLoginObj.matchOrigin(event.origin) && QRLoginObj.matchData(event.data)) {
            const loginTmpCode = event.data.tmp_code;

            if (loginTmpCode && callbackIframeRef.current) {
              callbackIframeRef.current.src = buildFeishuIframeSrc(
                gotoUrl,
                loginTmpCode
              );
            }
          }
        };

        window.addEventListener("message", handleMessage, false);
      }
    };

    const scriptId = "feishu-qr-login-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://lf-package-cn.feishucdn.com/obj/feishu-static/lark/passport/qrcode/LarkSSOSDKWebQRCode-1.0.3.js";
      script.onload = () => {
        initFeishuLogin();
      };
      document.head.appendChild(script);
    } else {
      initFeishuLogin();
    }

    return () => {
      if (handleMessage) {
        window.removeEventListener("message", handleMessage, false);
      }
      const container = document.getElementById("pudu-onelogin-feishu-qrcode-root");
      if (container) {
        container.innerHTML = "";
      }
      if (callbackIframe) {
        callbackIframe.src = "about:blank";
      }
    };
  }, [appId, inFeishuClient, query]);

  if (inFeishuClient) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: "calc(-13.91vh + 40px)"
      }}
    >
      <div
        className="pudu-onelogin-feishu-qrcode-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <img
          src={FeishuIcon}
          alt="飞书"
          style={{ width: 20, height: 20, display: "block" }}
        />
        <span
          style={{
            color: "#10141a",
            fontSize: 18,
            fontWeight: 400,
            lineHeight: "26px",
          }}
        >
          {title}
        </span>
      </div>
      <div id="pudu-onelogin-feishu-qrcode-root" style={{ display: 'flex', flex: 'row', justifyContent: 'center' }}>
      </div>
      <iframe
        ref={callbackIframeRef}
        title="feishu-login-redirect"
        style={{ display: "none" }}
      />
    </div>
  );
};

export default FeishuLogin;
